import { useState, useCallback } from "react";
import { Mail, X, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { forgotPassword } from "../api/auth";

/**
 * useForgotPassword
 * Same drop-in pattern as useConfirm() / useChangePassword().
 * Usage:
 *   const { openForgotPassword, forgotPasswordState, closeForgotPassword } = useForgotPassword();
 *   <button onClick={openForgotPassword}>Forgot Password?</button>
 *
 * Render <ForgotPasswordDialog forgotPasswordState={forgotPasswordState} onClose={closeForgotPassword} />
 * once, near the top of the component's returned JSX.
 */
export function useForgotPassword() {
  const [forgotPasswordState, setForgotPasswordState] = useState(null);

  const openForgotPassword = useCallback(() => {
    setForgotPasswordState({});
  }, []);

  const closeForgotPassword = useCallback(() => {
    setForgotPasswordState(null);
  }, []);

  return { openForgotPassword, forgotPasswordState, closeForgotPassword };
}

/**
 * ForgotPasswordDialog - collects an email and asks the backend to send a
 * reset link to it.
 */
export default function ForgotPasswordDialog({ forgotPasswordState, onClose }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  if (!forgotPasswordState) return null;

  const resetAndClose = () => {
    setEmail("");
    setError("");
    setSuccess("");
    setLoading(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setError("Invalid email address");
      return;
    }

    try {
      setLoading(true);
      const { data } = await forgotPassword(email.trim());
      setSuccess(data.message || "A password reset link has been sent to your email.");
    } catch (err) {
      setError(
        err.response?.data?.message || "Unable to send reset link. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0 w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
              <Mail size={18} className="text-blue-600" />
            </div>
            <p className="text-sm font-bold text-slate-700">Reset your password</p>
          </div>
          <button
            onClick={resetAndClose}
            aria-label="Close"
            className="shrink-0 text-slate-400 hover:text-slate-600 transition"
          >
            <X size={16} />
          </button>
        </div>

        <p className="mt-2 text-xs text-slate-500">
          Enter the email address on your account and we'll send you a link to reset your password.
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3.5">
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-xs font-medium">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-lg text-xs font-medium">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {!success && (
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-600">Email Address</label>
              <div className="relative rounded-lg border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                <input
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-3 pr-3 py-2.5 rounded-lg outline-none bg-transparent text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>
            </div>
          )}

          <div className="pt-1 flex justify-end gap-2.5">
            <button
              type="button"
              onClick={resetAndClose}
              className="px-4 py-2 rounded-lg text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              {success ? "Close" : "Cancel"}
            </button>
            {!success && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}